import { useGetAllEmployeesQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import {
  useGenerateLeaveEncashmentMutation,
  useGetFiscalYearsQuery,
} from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import EmployeeSelect, {
  type EmployeeMinimal,
} from '@/components/EmployeeSelect';
import { ChevronDownIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form, Select } from 'antd';
import { flattenAssigned } from '../shared/payroll.helpers';

type Props = {
  open: boolean;
  handleClose: () => void;
};

/** First fiscal year offered in the picker. */
const FIRST_FISCAL_YEAR = 2020;

/**
 * Generate Leave Encashment: pick a fiscal year and the employees whose unused
 * leave is paid out. The batch is built in the background.
 */
const ManageEncashment = ({ open, handleClose }: Props) => (
  <CustomDrawer
    width={800}
    open={open}
    onClose={handleClose}
    title="Generate Leave Encashment"
  >
    {/* Mounted only while open, so every run starts clean. */}
    {open && <EncashmentForm onDone={handleClose} />}
  </CustomDrawer>
);

const EncashmentForm = ({ onDone }: { onDone: () => void }) => {
  const [form] = Form.useForm();
  const assignedEmployees = Form.useWatch('assigned_employees', form);

  const { data: employeeData } = useGetAllEmployeesQuery({
    params: { skip: 0, limit: 0, status: 'active' },
  });
  const employees = (employeeData?.data?.data || []) as EmployeeMinimal[];

  const { data: fiscalYearsData } = useGetFiscalYearsQuery({
    previous_year: FIRST_FISCAL_YEAR,
  });

  const [generateEncashment, { isLoading: isGenerating }] =
    useGenerateLeaveEncashmentMutation();

  const handleSubmit = async (values: {
    fiscal_year: number;
    assigned_employees?: Record<string, string[]>;
  }) => {
    const userIds = flattenAssigned(values.assigned_employees);
    if (!userIds.length) {
      form.setFields([
        { name: 'assigned_employees', errors: ['Please select employees'] },
      ]);
      return;
    }

    try {
      await generateEncashment({
        fiscal_year: values.fiscal_year,
        user_ids: userIds,
      }).unwrap();
      toast('success', 'Leave encashment generation process has started.');
      onDone();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to generate encashment'));
    }
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form space-y-6"
      onFinish={handleSubmit}
    >
      <Form.Item initialValue={{}} noStyle name="assigned_employees" />

      <div className="grid grid-cols-2 gap-6">
        <Form.Item
          label="Running Fiscal Year"
          name="fiscal_year"
          rules={[{ required: true, message: 'Please select fiscal year' }]}
        >
          <Select
            placeholder="Select"
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            options={fiscalYearsData?.data?.map((year) => ({
              label: year.ui,
              value: year.payload,
            }))}
          />
        </Form.Item>
      </div>

      <EmployeeSelect form={form} employees={employees} />
      <EmployeeSelect.Selected
        form={form}
        employees={employees}
        allowRemoveEmployee
        assignedEmployees={assignedEmployees || {}}
      />

      <Flex justify="end">
        <Button
          htmlType="submit"
          loading={isGenerating}
          className="btn-primary min-w-[80px] h-11 font-semibold"
        >
          Generate
        </Button>
      </Flex>
    </Form>
  );
};

export default ManageEncashment;
